import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '@scalero-inc/forno-button';
import ButtonGroup from '@scalero-inc/forno-button-group';
import Dropdown from '@scalero-inc/forno-dropdown';
import Spinner from '@scalero-inc/forno-spinner';
import Pagination from 'components/pagination';
import FilesContainer from 'containers/files-container';
import { useTemplatesPaginatedQuery } from 'hooks/api/templates';
import { useUserQuery } from 'hooks/api/users';
import { FiDollarSign, FiPlus } from 'react-icons/fi';
import {
  canEditOnProFeatures,
  hasAccessToProFeatures,
} from 'utils/permissions';
import { sortingOptions, viewOptions } from './constants';

import styles from './styles.module.css';

function DashboardTemplates({ teamId, projectId }) {
  const customClassname = classNames(styles['dashboard-templates']);
  const navigate = useNavigate();

  const [view, setView] = useState('grid');
  const onViewChange = useCallback(
    (event) => {
      const newView = event[0];
      setView(newView);
    },
    [setView]
  );

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalTemplates, setTotalTemplates] = useState(0);
  const defaultOrder = sortingOptions[0];
  const [order, setOrder] = useState(defaultOrder.value);
  const queryOrder =
    order === defaultOrder.value ? '-created_at' : 'created_at';

  const templatesQuery = useTemplatesPaginatedQuery({
    config: {
      reFetchOnWindowFocus: false,
    },
    queryOrder,
    queryPage: page,
    pageSize,
    teamId,
    projectId,
  });
  const {
    isLoading: isTemplatesLoading,
    isFetching: isTemplatesFetching,
    data: templatesData,
  } = templatesQuery;

  const onPageChange = useCallback(
    (pageNumber) => {
      setPage(pageNumber);
    },
    [setPage]
  );
  const onPageSizeChange = useCallback(
    (size) => {
      setPageSize(size);
      setPage(1);
    },
    [setPageSize, setPage]
  );
  const newTemplate = () => {
    const url =
      teamId && projectId
        ? `/dashboard/teams/${teamId}/projects/${projectId}/templates/create`
        : `/dashboard/personal/templates/create`;
    navigate(url);
  };

  useEffect(() => {
    if (templatesData) setTotalTemplates(templatesData.count);
  }, [templatesData]);

  const { isLoading: isUserLoading, data: user } = useUserQuery();
  if (isUserLoading) return <Spinner />;

  const isEditable = canEditOnProFeatures(user, teamId);
  const isBlocked = !hasAccessToProFeatures({ user });
  const changePlanUrl = `/change-plan`;

  if (isBlocked) {
    return (
      <>
        <p>
          Only pro and team subscriptions have access to templates. Please
          upgrade your plan.
        </p>
        <Link
          to={changePlanUrl}
          className="forno-button font-block-m-sb"
          data-size="m"
          data-hierarchy="primary"
          data-theme="light"
        >
          <FiDollarSign />
          Change plan
        </Link>
      </>
    );
  }

  return (
    <div className={customClassname}>
      <div className={styles['dashboard-templates-actions']}>
        <Button onClick={newTemplate} icon={FiPlus} disabled={!isEditable}>
          New Template
        </Button>
        <div className={styles['dashboard-templates-right-actions']}>
          <div className={styles['dashboard-templates-action-wrapper']}>
            <span>Sort By:</span>
            <Dropdown
              size="s"
              theme="light"
              options={sortingOptions}
              defaultValue={defaultOrder}
              className={styles['dashboard-templates-sort-dropdown']}
              onChange={(e) => {
                setOrder(e.value);
              }}
              value={order}
              look="text"
            />
          </div>
          <div className={styles['dashboard-templates-action-wrapper']}>
            <span>View: </span>
            <ButtonGroup
              type="radio"
              theme="light"
              options={viewOptions}
              onChange={onViewChange}
              initialValue={[view]}
              className={styles['dashboard-templates-view-buttons']}
            >
              {viewOptions.map(({ value, icon: Icon }) => {
                return (
                  <ButtonGroup.Button key={value} value={value}>
                    <Icon />
                  </ButtonGroup.Button>
                );
              })}
            </ButtonGroup>
          </div>
        </div>
      </div>

      <div className={styles['dashboard-templates-container']}>
        <FilesContainer
          type="templates"
          teamId={teamId}
          projectId={projectId}
          view={view}
          files={templatesData?.results || []}
          page={page}
          setPage={onPageChange}
          isBlocked={isBlocked}
          isEditable={isEditable}
          isLoading={isTemplatesLoading || isTemplatesFetching}
        />
      </div>

      <Pagination
        total={totalTemplates}
        current={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
}

DashboardTemplates.propTypes = {
  teamId: PropTypes.string,
  projectId: PropTypes.string,
};

DashboardTemplates.defaultProps = {
  teamId: null,
  projectId: null,
};

export default DashboardTemplates;
